import React, { useEffect, useState, useCallback, useContext } from 'react';
import * as Yup from "yup";
import { Box, Typography, Button, Divider, Modal, IconButton, Stack, CircularProgress, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from 'axios';
import { notificationService } from '../../service/FCM/NotificationService';
import { getCancelReasons, createCancelInvoice, viewOrderDetails } from '../../service/order/orderService';
import SelectFieldWithSearch from '../Form/SelectFieldWithSearch';
import { Formik, Form } from "formik";
import { notifyError, notifySuccess } from '../ToastContainer/Toast/Toast';
import { OrderCountsContext } from '../../Pages/Orders/Tabs/InvoiceTabs';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  overflow: 'hidden',
};

interface CancelOrderModalProps {
  open: boolean;
  onClose: () => void;
  orderData: any;
  onCancelSuccess: () => void;
}

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  open,
  onClose,
  orderData,
  onCancelSuccess,
}) => {
  const [cancelReasons, setCancelReasons] = useState<{ label: string; value: string }[]>([]);
  // Safely access context - may be null if not within OrderCountsProvider
  const orderCountsContext = useContext(OrderCountsContext);
  const decrementCount = orderCountsContext?.decrementCount;

  useEffect(() => {
    const fetchCancelReasons = async () => {
      try {
        const res = await getCancelReasons();
        if (res?.data?.data) {
          const formatted = res.data.data.map((item: any) => ({
            label: item.reason, // UI display
            value: item.id,    // backend payload id
          }));
          setCancelReasons(formatted);
        }
      } catch (error) {
        console.error("Failed to fetch cancel reasons:", error);
      }
    };

    if (open) {
      fetchCancelReasons();
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: "1.2rem" }}
            >
              Cancel Order
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              {orderData?.id} | {orderData?.order_date ? new Date(orderData.order_date).toLocaleTimeString() : ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ p: 0.5 }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>

        <Divider sx={{ my: 0, mx: 0, bgcolor: "#e0e0e0" }} />

        {/* Formik Form */}
        <Formik
          initialValues={{ cancelReason: "" }}
          validationSchema={Yup.object({
            cancelReason: Yup.string().required("Please select a reason"),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (!orderData) {
                notifyError("No order data available to process.");
                return;
              }
              // If order_items is missing (FCM fallback data), fetch full order details first
              let finalOrderData = orderData;
              if (!orderData.order_items || orderData.order_items.length === 0) {
                const orderId = orderData.id || (orderData as any).orderId;
                console.log('[CancelOrderModal] order_items missing, fetching full order details for:', orderId);
                const res = await viewOrderDetails(orderId, false);
                const data = res?.data?.data;
                if (data && data.order_items) {
                  finalOrderData = data;
                } else {
                  notifyError("Could not load order details. Please try again.");
                  return;
                }
              }

              const payload = {
                order_id: finalOrderData.id,
                items: finalOrderData.order_items.map((item: any) => ({
                  item_id: item.id,
                  product_id: item.product_id,
                  quantity: item.quantity,
                })),
                cancel_reason_id: values.cancelReason,
                cancel_entire_order: false,
                type: "cancel"
              };

              try {
                const response = await createCancelInvoice(payload);
                if (response?.status === 201) {
                  notifySuccess(`Order ${orderData.id} cancelled successfully!`);
                  
                  // Update counts: decrement 'new' count when order is cancelled (if context is available)
                  if (decrementCount) {
                    decrementCount('new');
                  }
                  
                  // Close modal and process next order (this will stop sound and navigate)
                  notificationService.rejectOrder(orderData.id.toString());
                  onCancelSuccess();
                  onClose();
                } else {
                  notifyError(response?.data?.message);
                }
              } catch (error) {
                // When the API is in-flight and navigation happens (e.g. FCM confirms order status changed),
                // abortInFlightRequests() cancels the response. The order WAS processed — show success.
                if (axios.isCancel(error)) {
                  console.log('[CancelOrderModal] Request cancelled due to navigation — order processed successfully');
                  notifySuccess(`Order cancelled successfully!`);
                  notificationService.rejectOrder((orderData.id || (orderData as any).orderId).toString());
                  onCancelSuccess();
                  onClose();
                  return;
                }
                notifyError(error?.response?.data?.message || "Failed to cancel the order. Please try again.");
                onClose();
              }
            } catch (error) {
              console.error("Cancel Failed:", error);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, setFieldValue, errors, touched, isSubmitting }) => (
            <Form>
              {/* Body */}
              <Box sx={{ px: 3, py: 2, backgroundColor: "white" }}>
                <Typography
                  fontWeight={600}
                  sx={{ fontSize: "0.95rem", mb: 1 }}
                >
                  Are you sure to cancel this order?
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  If Yes, Select Reason
                </Typography>

                {/* SelectFieldWithSearch */}
                <SelectFieldWithSearch
                  label=""
                  name="cancelReason"
                  value={values.cancelReason}
                  onChange={(val) => {
                    setFieldValue("cancelReason", val.target.value ?? val);
                  }}
                  options={cancelReasons}
                  placeholder="Select Reason"
                  size='large'
                />
              </Box>

              {/* Actions */}
              <Stack
                direction="row"
                spacing={2}
                sx={{ mb: 3, mx: 3, mt: 2 }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    textTransform: "none",
                    py: 1.5,
                    borderRadius: 1,
                    borderColor: "#e0e0e0",
                    color: "black",
                  }}
                  disabled={isSubmitting}
                >
                  Discard
                </Button>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={!values.cancelReason || isSubmitting}
                  sx={{
                    bgcolor: "black",
                    textTransform: "none",
                    py: 1.5,
                    borderRadius: 1,
                    color: "white",
                    "&:hover": { bgcolor: "#333" },
                  }}
                >
                  {isSubmitting ? "Cancelling..." : "Cancel"}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default CancelOrderModal;
